import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { safeNavigate } from '../utils/security';
import api from '../utils/api';

const SUB_ICONS = {
  Necklaces:'fa-circle-nodes', Earrings:'fa-moon', Bangles:'fa-ring', Rings:'fa-ring',
  Anklets:'fa-link', Mangalsutra:'fa-heart', 'Maang Tikka':'fa-crown', 'Nose Pins':'fa-circle',
  Shampoo:'fa-bottle-droplet', Conditioner:'fa-droplet', Serums:'fa-flask', 'Hair Oils':'fa-oil-well',
  Treatment:'fa-syringe', 'Hair Color':'fa-palette',
  Sarees:'fa-scroll', Kurtis:'fa-shirt', 'Dress Material':'fa-scissors', Lehengas:'fa-crown',
  Blouses:'fa-tshirt', Accessories:'fa-bag-shopping',
  Decor:'fa-couch', Pooja:'fa-sun', 'Gift Sets':'fa-gift', Lamps:'fa-lightbulb', 'Wall Art':'fa-image', Textiles:'fa-rug',
};

function MegaMenu({ categories, products }) {
  const [activeCat, setActiveCat] = useState(0);
  const cat = categories[activeCat];
  const catProducts = products.filter(p => p.category === cat.name).slice(0, 5);

  return (
    <div className="mega-menu">
      <div className="mega-left">
        <p className="mega-cat-header">Categories</p>
        {categories.map((c, i) => (
          <button
            key={c.id}
            className={`mega-cat-btn ${i === activeCat ? 'active' : ''}`}
            onMouseEnter={() => setActiveCat(i)}
          >
            <i className={`fas ${c.icon}`} />
            {c.name}
            <span className="cat-count">{c.count}</span>
          </button>
        ))}
        
        {/* Indha div-ku puthiya class name koduthullom */}
        <div className="mega-view-all">
          <Link to="/shop" className="mega-view-all-link">
            <i className="fas fa-store" /> View All Products
          </Link>
        </div>
      </div>

      <div className="mega-right">
        {categories.map((c, i) => (
          <div key={c.id} className={`mega-content ${i === activeCat ? 'active' : ''}`}>
            <div className="mega-content-header">
              <p className="mega-cat-desc">
                {c.description}
              </p>
            </div>
            <div className="mega-subs-grid">
              {c.subcategories.map(sub => (
                <Link
                  key={sub}
                  to={`/shop?category=${c.slug}&sub=${encodeURIComponent(sub)}`}
                  className="mega-sub-link"
                >
                  <i className={`fas ${SUB_ICONS[sub] || 'fa-chevron-right'}`} />
                  {sub}
                </Link>
              ))}
            </div>
            {catProducts.length > 0 && (
              <>
                <p className="mega-products-label">
                  <i className="fas fa-star mega-star-icon" />
                  Top in {c.name}
                </p>
                <div className="mega-products-scroll">
                  {catProducts.map(p => (
                    <Link key={p.id} to={`/product/${p.id}`} className="mega-product-chip">
                      <img src={p.images.front} alt={p.name} />
                      <p>{p.name.length > 20 ? p.name.slice(0,18)+'…' : p.name}</p>
                      <p className="chip-price">₹{p.price.toLocaleString('en-IN')}</p>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchOverlay({ open, onClose, products }) {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) { setQ(''); setTimeout(() => inputRef.current?.focus(), 80); }
  }, [open]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const results = q.trim().length > 1
    ? products.filter(p =>
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        p.category.toLowerCase().includes(q.toLowerCase())
      ).slice(0, 6)
    : [];

  const handleSelect = (id) => { onClose(); safeNavigate(navigate, `/product/${id}`); };

  return (
    <div className={`search-overlay ${open ? 'open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <button className="search-overlay-close" onClick={onClose} aria-label="Close search">
        <i className="fas fa-times" />
      </button>
      <div className="search-overlay-inner">
        <div className="search-input-wrapper">
          <input
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search jewellery, sarees, hair care…"
            onKeyDown={e => { if (e.key === 'Enter' && results[0]) handleSelect(results[0].id); }}
          />
          <i className="fas fa-search search-icon" />
        </div>
        {results.length > 0 && (
          <div className="search-results-list">
  {results.map(p => (
          <div 
            key={p.id} 
            className="search-result-item" 
            onClick={() => handleSelect(p.id)} 
            role="button" 
            tabIndex={0} 
            onKeyDown={e => e.key === 'Enter' && handleSelect(p.id)}
          >
            <img className="search-result-img" src={p.images.front} alt={p.name} />
            <div className="search-result-info">
              <p className="search-result-name">{p.name}</p>
              <p className="search-result-cat">{p.category} · {p.subcategory}</p>
            </div>
            <span className="search-result-price">₹{p.price.toLocaleString('en-IN')}</span>
          </div>
        ))}
      </div>
        )}
        {q.length > 1 && results.length === 0 && (
          <p className="no-search-results">
            No results for "{q}"
          </p>
        )}
      </div>
    </div>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { cartCount, wishlist } = useStore();
  const { isAuthenticated, user, logout } = useAuth();
  const isLoggedIn = !!(isAuthenticated || user);
  const navigate = useNavigate();
  
  const location = useLocation();
  const [menuKey, setMenuKey] = useState(0); 

  const [hideTopbar, setHideTopbar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const freeShippingAbove = storeSettings?.shippingAlerts?.freeShippingAbove ?? 999;

  useEffect(() => {
    api.get('/store-settings')
      .then(res => {
        const data = res?.data.data || res || {};
        setStoreSettings(data);
      })
      .catch(err => {
        console.error('Failed to fetch store settings:', err);
        setStoreSettings(null);
      });
  }, []);

  useEffect(() => {
    api.get('/products/categories')
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data.categories || [];
        if (data.length) setCategories(data);
      })
      .catch(() => { /* keep localCategories fallback */ });
  }, []);

  useEffect(() => {
    api.get('/products')
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data.products || res.data.data || [];
        if (data.length) setProducts(data);
      })
      .catch(() => { /* keep localProducts fallback */ });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY;
      
      setScrolled(currentScrollY > 40);

      if (currentScrollY > 40) {
        if (currentScrollY > lastScrollY) {
          setHideTopbar(true);  
        } else {
          setHideTopbar(false); 
        }
      } else {
        setHideTopbar(false);   
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [lastScrollY]);

  useEffect(() => {
    document.body.style.overflow = (mobileOpen || searchOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen, searchOpen]);

  useEffect(() => {
    setMobileOpen(false);
    setMenuKey(prev => prev + 1); 
  }, [location.pathname, location.search]);

  return (
    <>
      <header key={menuKey} className={`site-header ${scrolled ? 'scrolled' : 'transparent'}`}>
      <div className='headerBg'>        
        {storeSettings && (
        <div className={`ps-topbar d-none d-md-block ${hideTopbar ? 'topbar-hidden' : ''}`}>
          <div className="container d-flex justify-content-between align-items-center">
            <span>
              <i className="fas fa-shipping-fast me-1" /> Free shipping on orders above ₹{freeShippingAbove.toLocaleString('en-IN')}
            </span>
            <span>
              <a href={`tel:${storeSettings.storeInfo?.phone}`}>
                <i className="fas fa-phone me-1" />{storeSettings.storeInfo?.phone}
              </a>
              <span className="topbar-divider">|</span>
              <a href={`mailto:${storeSettings.storeInfo?.email}`}>
                <i className="fas fa-envelope me-1" />{storeSettings.storeInfo?.email}
              </a>
            </span>
          </div>
        </div>
        )}

        <div className="container">
          <div className="header-inner"> 
            <Link to="/" className="header-logo" onClick={() => setMobileOpen(false)}>
              <img src={process.env.PUBLIC_URL + "/images/logo.png"} alt="PandianStore" />
            </Link>

            {/* Desktop Nav */}
            <nav className="header-nav" aria-label="Main navigation">
              <div className="nav-item">
                <NavLink to="/" end className={({isActive}) => `nav-link${isActive?' active':''}`}>Home</NavLink>
              </div>

              <div className="nav-item">
                <NavLink to="/shop" end className={({isActive}) => `nav-link${isActive?' active':''}`}>Shop</NavLink>
              </div>

              {/* Category links with mega */}
              {categories.map(cat => (
                <div className="nav-item" key={cat.id}>
                  <Link to={`/shop?category=${cat.slug}`} className="nav-link">
                    {cat.name} <i className="fas fa-chevron-down chevron" />
                  </Link>
                  <div className="mega-menu-wrap" style={{minWidth:560}}>
                    <div className="mega-menu" style={{minWidth:560}}>
                      <div className="mega-left" style={{width:180}}>
                        <p className="mega-cat-header">{cat.name}</p>
                        {cat.subcategories.map(sub => (
                          <Link 
                            key={sub} 
                            to={`/shop?category=${cat.slug}&sub=${encodeURIComponent(sub)}`} 
                            className="mega-cat-btn mega-cat-link"
                          >
                            <i className={`fas ${SUB_ICONS[sub]||'fa-chevron-right'}`} />
                            {sub}
                          </Link>
                        ))}
                      </div>
                      <div className="mega-right">
                        <div className="mega-content active">
                          <p className="mega-description">{cat.description}</p>
                          {(() => {
                            const catProducts = products
                              .filter((p) => p.category === cat.name)
                              .slice(0, 5);
                            if (!catProducts.length) return null;
                            return (
                              <>
                                <p className="mega-products-label">
                                  <i className="fas fa-star mega-star-icon" />
                                  Top Products
                                </p>
                                <div className="mega-products-scroll">
                                  {catProducts.map((p) => {
                                    const imgSrc = p.images?.front || p.image || '';
                                    const src = imgSrc.startsWith('data:') || imgSrc.startsWith('http')
                                      ? imgSrc
                                      : `${process.env.PUBLIC_URL}${imgSrc}`;
                                    return (
                                      <Link key={p._id || p.id} to={`/product/${p._id || p.id}`} className="mega-product-chip">
                                        <img src={src} alt={p.name} loading="lazy" />
                                        <p>{p.name.length > 20 ? p.name.slice(0, 18) + '…' : p.name}</p>
                                        <p className="chip-price">₹{p.price.toLocaleString('en-IN')}</p>
                                      </Link>
                                    );
                                  })}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <span className="nav-divider">|</span>
              
              {/* Trending */}
              <div className="nav-item">
                <NavLink to="/trending" className={({isActive}) => `nav-link ${isActive?' active':''}`}>
                  🔥Trending
                  <span className="trending-pill">HOT</span>
                </NavLink>
              </div>
            </nav>

            {/* Actions */}
            <div className="header-actions">
              <button className="header-icon-btn" onClick={() => setSearchOpen(true)} aria-label="Search">
                <i className="fas fa-search" />
              </button>
              <Link to="/wishlist" className="header-icon-btn" aria-label="Wishlist">
                <i className="far fa-heart" />
                {wishlist.length > 0 && <span className="badge">{wishlist.length}</span>}
              </Link>
              <Link to="/cart" className="header-icon-btn" aria-label="Cart">
                <i className="fas fa-bag-shopping" />
                {cartCount > 0 && <span className="badge">{cartCount}</span>}
              </Link>
              
              {/* Authentication Buttons */}
              {isLoggedIn ? (
                <>
                  <Link to="/dashboard" className="header-icon-btn" aria-label="Account" title={user?.name}>
                    <i className="far fa-user" />
                  </Link>
                  <button 
                    className="header-icon-btn" 
                    aria-label="Logout"
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                    title="Logout"
                  >
                    <i className="fas fa-sign-out-alt" />
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="header-icon-btn" aria-label="Login">
                    <i className="fas fa-sign-in-alt" />
                  </Link>
                  <Link to="/register" className="header-icon-btn" aria-label="Register">
                    <i className="fas fa-user-plus" />
                  </Link>
                </>
              )}
              
              <button className="header-icon-btn d-lg-none" onClick={() => setMobileOpen(o => !o)} aria-label="Menu" aria-expanded={mobileOpen}>
                <i className={`fas ${mobileOpen ? 'fa-times' : 'fa-bars'}`} />
              </button>
            </div>
          </div>
        </div> {/* Container wrapper closed */}
    </div>
      </header>

      {/* Mobile Drawer */}
      <nav className={`mobile-drawer ${mobileOpen ? 'open' : ''}`} aria-label="Mobile navigation">
        {[{to:'/',label:'Home',icon:'fa-house'},{to:'/shop',label:'Shop',icon:'fa-store'},{to:'/trending',label:'Trending 🔥',icon:'fa-fire'},{to:'/catalogue',label:'Catalogue',icon:'fa-book-open'},{to:'/wishlist',label:'Wishlist',icon:'fa-heart'},{to:'/cart',label:'Cart',icon:'fa-bag-shopping'},{to:'/dashboard',label:'My Account',icon:'fa-user'}].map(item => (
          <Link key={item.to} to={item.to} className="mobile-nav-link" onClick={() => setMobileOpen(false)}>
            <i className={`fas ${item.icon}`} />{item.label}
          </Link>
        ))}
        
        {/* Authentication Section */}
        {isLoggedIn ? (
          <button 
            className="mobile-nav-link"
            onClick={() => {
              logout();
              navigate('/');
              setMobileOpen(false);
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
          >
            <i className="fas fa-sign-out-alt" />Sign Out
          </button>
        ) : (
          <>
            <Link to="/login" className="mobile-nav-link" onClick={() => setMobileOpen(false)}>
              <i className="fas fa-sign-in-alt" />Login
            </Link>
            <Link to="/register" className="mobile-nav-link" onClick={() => setMobileOpen(false)}>
              <i className="fas fa-user-plus" />Register
            </Link>
          </>
        )}
        
        <p className="mobile-section-label">Categories</p>
        {categories.map(cat => (
          <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="mobile-nav-link" onClick={() => setMobileOpen(false)}>
            <i className={`fas ${cat.icon}`} />{cat.name}
            <span className="mobile-cat-count">{cat.count}</span>
          </Link>
        ))}
      </nav>

      {/* Catalogue fixed */}
      <Link to="/catalogue" className="catalogue-fixed-btn" aria-label="View Catalogue">
        <i className="fas fa-book-open" />Catalogue
      </Link>

      {/* Search overlay */}
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} products={products} />
    </>
  );
}